import { Group, type Scene, type Texture } from 'three';
import { Pcg32 } from '../../../sim/rng';
import type { SimEvent, World } from '../../../sim/types';
import type { Sky } from '../../../sim/weather';
import type { PixelCamera } from '../../camera/PixelCamera';
import type { Palette } from '../../palette';
import { FootprintDecals } from '../decals/footprints';
import { bark, markerDrawer } from './barks';
import { disposeBatches, flushBatches, resetBatches } from './batch';
import { createBatchSet } from './batches';
import { explode, fireballDrawer, ScorchDecals, wreck } from './blast';
import type { EffectsToggles, Fx } from './context';
import { enemyHit, impact, rangerHit, throwPuff } from './impacts';
import { LightPool } from './lights';
import { Flashes, knife, rocketLaunch, shot } from './muzzle';
import { ParticleSystem } from './particles';
import { Projectiles } from './projectiles';
import { Shake } from './shake';

/*
 * Presentation-side effects (05 §9): sim events and live projectiles in, small flat-shaded
 * meshes and a few lights out. Everything is drawn through InstancedMesh batches, one per
 * palette colour, so a colour is never invented here and a busy firefight costs a handful
 * of draw calls. There is no alpha anywhere (05 §8.1): things fade by shrinking in steps.
 *
 * This file is the public face and the wiring; each concern lives in its own module.
 */

/** Simultaneous muzzle-flash and explosion lights; more requests than this reuse the weakest. */
const MAX_LIGHTS = 4;

export type { EffectsToggles } from './context';

export interface EffectsOptions {
  scene: Scene;
  palette: Palette;
  gradientMap: Texture;
  groundY: (x: number, z: number) => number;
  /** Where a prop stands, for `propDestroyed` (the event carries only its id). */
  propPosition?: (id: number) => { x: number; z: number } | null;
  /** Emission strength of self-lit effects: about 0.4 by day, 1.0 or more at night. Default 0.8. */
  emissiveBoost?: number;
  /** The mission's sky, so footprints fade on screen as fast as they fade in the sim. Default calm. */
  sky?: Sky;
}

/**
 * Turns sim events into one-shot effects and mirrors the world's live projectiles.
 *
 * Owns everything it adds to the scene (one group of instanced batches and a fixed pool of
 * point lights that idle at zero intensity, so the light count - and therefore every
 * compiled shader - never changes mid-mission).
 */
export class Effects {
  /** Whole-texel screen-shake offset in metres (x, z) for the camera focus this frame; decays by itself. */
  readonly shake: { x: number; z: number };

  private readonly scene: Scene;
  private readonly root = new Group();
  private readonly fx: Fx;
  private readonly flashes = new Flashes();
  private readonly projectiles: Projectiles;
  /** ROUND5 §4: the ranger's own trail, read straight off the world the soldiers track him by. */
  private readonly footprints: FootprintDecals;

  constructor(options: EffectsOptions) {
    const { palette, gradientMap, groundY } = options;
    this.scene = options.scene;
    // Presentation-only randomness, seeded so look checks repeat.
    const rng = new Pcg32(0x0a1b2c3d, 11);
    const set = createBatchSet(this.root, palette, gradientMap, options.emissiveBoost ?? 0.8);
    const particles = new ParticleSystem(rng, groundY, set.b.cubeGround);
    const muzzleColor = palette.color('fire.2');
    const shake = new Shake(rng);
    this.fx = {
      rng,
      groundY,
      propPosition: options.propPosition ?? null,
      set,
      particles,
      lights: new LightPool(this.root, muzzleColor, MAX_LIGHTS),
      shake,
      scorch: new ScorchDecals(set.b.scorch, rng, groundY),
      muzzleColor,
      blastColor: palette.color('fire.3'),
      toggles: { flashes: true, shake: true },
      world: null,
      alpha: 0,
    };
    this.shake = shake.offset;
    this.projectiles = new Projectiles(this.fx);
    this.footprints = new FootprintDecals(set.b.footprint, groundY, options.sky ?? { weather: 'clear', modifiers: [] });

    particles.setDrawer('fireball', fireballDrawer(set.fire, this.fx.toggles));
    particles.setDrawer('flash', this.flashes.drawFlash);
    particles.setDrawer('slash', this.flashes.drawSlash);
    particles.setDrawer('marker', markerDrawer(this.fx));

    this.root.name = 'effects';
    this.scene.add(this.root);
  }

  /**
   * Comfort options, live: `flashes: false` softens muzzle and blast light for photosensitive
   * players (see `EffectsToggles`), `shake: false` keeps the camera still. Both default to true.
   */
  setOptions(options: Partial<EffectsToggles>): void {
    const t = this.fx.toggles;
    if (options.flashes !== undefined) t.flashes = options.flashes;
    if (options.shake !== undefined) t.shake = options.shake;
    this.fx.lights.soft = !t.flashes;
    this.fx.shake.enabled = t.shake;
  }

  /** The options in force (a copy). */
  get options(): EffectsToggles {
    return { ...this.fx.toggles };
  }

  /** One-shot effects for this frame's sim events. */
  handle(events: readonly SimEvent[]): void {
    const fx = this.fx;
    for (const e of events) {
      switch (e.type) {
        case 'shot':
          shot(fx, e);
          break;
        case 'impact':
          impact(fx, e);
          break;
        case 'explosion':
          explode(fx, e);
          break;
        case 'throw':
          throwPuff(fx, e);
          break;
        case 'rocket':
          rocketLaunch(fx, e);
          break;
        case 'knife':
          knife(fx, e);
          break;
        case 'rangerHit':
          rangerHit(fx);
          break;
        case 'enemyHit':
          enemyHit(fx, e);
          break;
        case 'propDestroyed':
          wreck(fx, e);
          break;
        case 'bark':
          bark(fx, e);
          break;
        default:
          // Everything else (alerts, pickups, sirens, and whatever the contract grows next)
          // is for audio and UI. Deliberately not exhaustive: an unknown event is ignored.
          break;
      }
    }
  }

  /**
   * Per rendered frame: advance particles, and mirror the world's live projectiles
   * (bullets as tracers, thrown grenades, rockets with smoke trails, placed bombs with a
   * blinking light). `alpha` is the 0..1 interpolation factor between the previous and
   * current sim tick.
   */
  update(world: World, dt: number, alpha: number, camera: PixelCamera): void {
    const fx = this.fx;
    const step = Math.min(Math.max(dt, 0), 0.1);
    fx.world = world;
    fx.alpha = alpha;
    fx.shake.track(camera);
    this.flashes.aim(camera);

    resetBatches(fx.set.all);
    this.footprints.draw(world, camera);
    this.projectiles.mirror(world, step, alpha, camera);
    fx.particles.step(step, camera);
    flushBatches(fx.set.all);

    fx.lights.step(step);
    fx.shake.step(step, camera);
  }

  dispose(): void {
    const fx = this.fx;
    this.scene.remove(this.root);
    disposeBatches(fx.set.all);
    for (const material of fx.set.materials) material.dispose();
    fx.lights.dispose();
    this.root.clear();
    this.projectiles.clear();
    fx.particles.clear();
    fx.world = null;
  }
}
